import {
  YearEndModuleStatus,
  YearEndModuleStatusOptions,
  YearEndModuleType,
} from '../yearEndModuleStatus.slice'
import { Text, Accordion, Icon } from '@/componentsV1/BaseComponents'
import { useMemo, useState } from 'react'
import VerifyTaxEntityCard from './VerifyTaxEntityCard'
import EoyBookkeepingCard from './EOYBookkeepingCard'
import EOYBookkeepingFollowupCard from './EOYBookkeepingFollowupCard'
import TaxQuestionnaireCard from './TaxQuestionnaireCard'
import SubmitExtensionRequestCard from './SubmitExtensionRequestCard'
import FileExtensionRequestCard from './FileExtensionRequestCard'
import File1040Card from './File1040Card'
import File1120SCard from './File1120SCard'
import File1099NECCard from './File1099NECCard'
import { faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import {
  faHourglass,
  faLock,
  faCheck,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons'
import styled from 'styled-components'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { Colors } from '../../../styles/theme'
import { TaxFormType } from '@/features/taxes/annual-taxes/questionnaires/constants'
import { ExtensionReason } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { selectTaxDetailsByYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { useReselector } from '@/utils/sharedHooks'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { selectUserTaxDocumentsForCategory } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
interface BaseYearEndModuleProgressListProps {
  taxYear: string
  annualTaxFormNeeds?: Array<TaxFormType> | null
  extensionReason?: ExtensionReason | null
  mostUrgentModule: YearEndModuleType | null
}
interface YearEndModuleInternalProgressListProps extends BaseYearEndModuleProgressListProps {
  yearEndModules: YearEndModuleStatus[]
  endLineOnLast?: boolean
}
export interface YearEndModuleProgressListProps extends BaseYearEndModuleProgressListProps {
  completeModules: YearEndModuleStatus[]
  incompleteModules: YearEndModuleStatus[]
}

const getComponentForYearEndModule = ({
  yeModule,
  taxYear,
  annualTaxFormNeeds,
  extensionReason,
  form1040PaymentDueDate,
  balanceSheetFileDownloadLink,
  profitLossFileDownloadLinks,
  generalLedgerFileDownloadLink,
}: BaseYearEndModuleProgressListProps & {
  yeModule: YearEndModuleStatus
  form1040PaymentDueDate?: string | null
  balanceSheetFileDownloadLink?: { url: string; title: string; id: number }
  profitLossFileDownloadLinks?: { url: string; title: string; id: number }[]
  generalLedgerFileDownloadLink?: { url: string; title: string; id: number }
}) => {
  const isTwoFormFiler = Boolean(
    annualTaxFormNeeds && annualTaxFormNeeds.length > 1
  )
  const isScorp = annualTaxFormNeeds?.includes(TaxFormType.form1120s)
  const props = {
    ...yeModule,
    taxYear,
  }
  switch (yeModule.moduleType) {
    case YearEndModuleType.verifyTaxEntity:
      return <VerifyTaxEntityCard {...props} />
    case YearEndModuleType.file1099Nec:
      return <File1099NECCard {...props} />
    case YearEndModuleType.eoyBookkeeping:
      return <EoyBookkeepingCard {...props} isScorp={isScorp} />
    case YearEndModuleType.eoyBookkeepingFollowup:
      return (
        <EOYBookkeepingFollowupCard
          {...props}
          isScorp={isScorp}
          balanceSheetFileDownloadLink={balanceSheetFileDownloadLink}
          profitLossFileDownloadLinks={profitLossFileDownloadLinks}
          generalLedgerFileDownloadLink={generalLedgerFileDownloadLink}
        />
      )
    case YearEndModuleType.taxQuestionnaire: {
      const needs = annualTaxFormNeeds || []
      return (
        <TaxQuestionnaireCard
          {...props}
          annualTaxFormNeeds={needs}
          isScorp={isScorp}
        />
      )
    }
    case YearEndModuleType.submitExtensionRequest:
      return (
        <SubmitExtensionRequestCard
          {...props}
          extensionReason={extensionReason}
        />
      )
    case YearEndModuleType.fileExtensionRequest:
      return (
        <FileExtensionRequestCard
          {...props}
          paymentDueDate={form1040PaymentDueDate}
        />
      )
    case YearEndModuleType.file1040:
      return (
        <File1040Card
          {...props}
          isTwoFormFiler={isTwoFormFiler}
          paymentDueDate={form1040PaymentDueDate}
        />
      )
    case YearEndModuleType.file1120S:
      return <File1120SCard {...props} />
    default:
      yeModule.moduleType satisfies never
      return null
  }
}

interface StatusCircleProps {
  backgroundColor?: string
  borderColor?: string
  icon?: IconDefinition
  isMostUrgent?: boolean
  zeroTopMargin?: boolean
}

const statusCircleWidth = 32
const StatusCircle = styled.div<StatusCircleProps>`
  width: ${statusCircleWidth}px;
  height: ${statusCircleWidth}px;
  border-radius: 50%;
  background-color: ${(props) => props.backgroundColor || Colors.white};
  ${(props) => props.borderColor && `border: 2px solid ${props.borderColor};`}
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  ${(props) =>
    props.isMostUrgent &&
    `&:after {
      content: " ";
      display: block;
      margin-left: 7rem;
      padding-right: 5rem;
      border-top: 2px solid ${props.borderColor || props.backgroundColor};
`}
`
const StatusLabel = (props: StatusCircleProps) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: props.zeroTopMargin ? 0 : 16,
      }}
    >
      <StatusCircle {...props}>
        {props.icon && <Icon color="white" icon={props.icon} />}
      </StatusCircle>
    </div>
  )
}

const getStatusColor = (status: YearEndModuleStatusOptions) => {
  switch (status) {
    case YearEndModuleStatusOptions.inProgress:
    case YearEndModuleStatusOptions.upNext:
      return Colors.orange
    case YearEndModuleStatusOptions.actionRequired:
      return Colors.yellow
    case YearEndModuleStatusOptions.waitingOnHeard:
      return Colors.blue
    case YearEndModuleStatusOptions.missed:
    case YearEndModuleStatusOptions.overdue:
      return Colors.red
    case YearEndModuleStatusOptions.cancelled:
    case YearEndModuleStatusOptions.upcoming:
      return Colors.mediumGray
    case YearEndModuleStatusOptions.complete:
      return Colors.accentGreen
    case YearEndModuleStatusOptions.earlyStart:
      return Colors.moss
    default:
      return status satisfies never
  }
}

const getStatusLabel = (
  status: YearEndModuleStatusOptions,
  isMostUrgent: boolean
) => {
  switch (status) {
    case YearEndModuleStatusOptions.inProgress:
    case YearEndModuleStatusOptions.upNext: {
      return (
        <StatusLabel
          borderColor={getStatusColor(status)}
          isMostUrgent={isMostUrgent}
        />
      )
    }
    case YearEndModuleStatusOptions.actionRequired: {
      return (
        <StatusLabel
          borderColor={getStatusColor(status)}
          isMostUrgent={isMostUrgent}
        />
      )
    }
    case YearEndModuleStatusOptions.waitingOnHeard: {
      return (
        <StatusLabel
          backgroundColor={getStatusColor(status)}
          icon={faHourglass}
        />
      )
    }
    case YearEndModuleStatusOptions.overdue: {
      return (
        <StatusLabel
          borderColor={getStatusColor(status)}
          isMostUrgent={isMostUrgent}
        />
      )
    }
    case YearEndModuleStatusOptions.earlyStart: {
      return (
        <StatusLabel
          borderColor={getStatusColor(status)}
          isMostUrgent={isMostUrgent}
        />
      )
    }
    case YearEndModuleStatusOptions.upcoming: {
      return (
        <StatusLabel backgroundColor={getStatusColor(status)} icon={faLock} />
      )
    }
    case YearEndModuleStatusOptions.complete: {
      return (
        <StatusLabel backgroundColor={getStatusColor(status)} icon={faCheck} />
      )
    }
    case YearEndModuleStatusOptions.missed: {
      return (
        <StatusLabel
          backgroundColor={getStatusColor(status)}
          isMostUrgent={isMostUrgent}
          icon={faXmark}
        />
      )
    }
    case YearEndModuleStatusOptions.cancelled: {
      return (
        <StatusLabel
          backgroundColor={getStatusColor(status)}
          isMostUrgent={isMostUrgent}
          icon={faXmark}
        />
      )
    }
    default:
      return status satisfies never
  }
}

const VerticalLineContainer = styled.div<{
  status: YearEndModuleStatusOptions
  last?: boolean
}>`
  background: ${(props) =>
    props.last
      ? 'none'
      : `linear-gradient(${Colors.gray}, ${Colors.gray}) no-repeat
    border-box left 50% top 16px /2px 100%;`};
  margin-bottom: -4rem;
  margin-right: 24px;
`
const YearEndItemContainer = styled.div`
  display: flex;
  padding: 1rem 0;
`

const YearEndListWithStatusIcons = (
  props: YearEndModuleInternalProgressListProps
) => {
  const { yearEndModules, mostUrgentModule, endLineOnLast } = props
  const shouldEndLineOnLast = endLineOnLast ?? true
  const moduleCount = yearEndModules.length
  const form1040PaymentDueDate = useReselector(
    selectTaxDetailsByYear,
    props.taxYear
  )?.form_1040_irs_due_date

  const balanceSheet = useReselector(
    selectUserTaxDocumentsForCategory,
    UserDocumentCategoryIdentifier.balanceSheet,
    props.taxYear
  )
  const profitAndLoss = useReselector(
    selectUserTaxDocumentsForCategory,
    UserDocumentCategoryIdentifier.profitAndLoss,
    props.taxYear
  )

  const balanceSheetFileDownloadLink = useMemo(
    () =>
      balanceSheet[0]
        ? {
            url: balanceSheet[0]?.signedUrl || '',
            title: balanceSheet[0]?.fileDescription || '',
            id: balanceSheet[0]?.id || 0,
          }
        : undefined,
    [balanceSheet]
  )

  const profitLossFileDownloadLinks = useMemo(
    () =>
      profitAndLoss.map((doc) => ({
        url: doc?.signedUrl || '',
        title: doc?.fileDescription || '',
        id: doc?.id || 0,
      })),
    [profitAndLoss]
  )

  const generalLedger = useReselector(
    selectUserTaxDocumentsForCategory,
    UserDocumentCategoryIdentifier.generalLedger,
    props.taxYear
  )

  const generalLedgerFileDownloadLink = useMemo(
    () =>
      generalLedger[0]
        ? {
            url: generalLedger[0]?.signedUrl || '',
            title: generalLedger[0]?.fileDescription || '',
            id: generalLedger[0]?.id || 0,
          }
        : undefined,
    [generalLedger]
  )

  return (
    <div>
      {yearEndModules.map((yeModule, i) => {
        const component = getComponentForYearEndModule({
          ...props,
          yeModule,
          form1040PaymentDueDate,
          balanceSheetFileDownloadLink,
          profitLossFileDownloadLinks,
          generalLedgerFileDownloadLink,
        })
        const isMostUrgent = mostUrgentModule === yeModule.moduleType
        return (
          <YearEndItemContainer key={yeModule.id}>
            <VerticalLineContainer
              status={yeModule.status}
              last={shouldEndLineOnLast && i === moduleCount - 1}
            >
              {getStatusLabel(yeModule.status, isMostUrgent)}
            </VerticalLineContainer>
            <div
              style={{
                flexGrow: 1,
              }}
            >
              {component}
            </div>
          </YearEndItemContainer>
        )
      })}
    </div>
  )
}

const CompletedModules = (props: YearEndModuleInternalProgressListProps) => {
  const { yearEndModules } = props
  const [accordionOpen, setAccordionOpen] = useState(false)
  const completedCount = yearEndModules.length
  const title = useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: 24,
          paddingBottom: accordionOpen ? 0 : '1rem',
        }}
      >
        {!accordionOpen && (
          <StatusLabel
            backgroundColor={getStatusColor(
              YearEndModuleStatusOptions.complete
            )}
            icon={faCheck}
            zeroTopMargin
          />
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 12,
            backgroundColor: Colors.stone40,
            borderRadius: 8,
            marginLeft: accordionOpen ? 55 : 24,
          }}
        >
          <Text as="h3" color="green">
            {accordionOpen
              ? 'Hide completed'
              : `Show ${completedCount} completed`}
          </Text>
          <Icon
            icon={accordionOpen ? faChevronUp : faChevronDown}
            style={{ marginLeft: 8 }}
            size="1x"
            color="green"
          />
        </div>
      </div>
    ),
    [accordionOpen, completedCount]
  )
  const content = useMemo(() => {
    return <YearEndListWithStatusIcons {...props} />
  }, [props])

  if (yearEndModules.length === 0) {
    return null
  }
  return (
    <Accordion
      variant="text"
      title={title}
      onOpenClick={setAccordionOpen}
      iconColor="transparent"
      content={content}
    />
  )
}

const YearEndModuleProgressList = (props: YearEndModuleProgressListProps) => {
  const { completeModules, incompleteModules, ...rest } = props

  const missedModules = incompleteModules.filter(
    (module) => module.status === YearEndModuleStatusOptions.missed
  )

  const activeIncompleteModules = incompleteModules.filter(
    (module) => module.status !== YearEndModuleStatusOptions.missed
  )

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Text as="h1">Your Progress</Text>

        <CompletedModules yearEndModules={completeModules} {...rest} />
        <YearEndListWithStatusIcons
          yearEndModules={activeIncompleteModules}
          {...rest}
        />
      </div>
      {missedModules.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: '32px',
          }}
        >
          <Text as="h1">Missed</Text>
          <Text as="bodySm" color="black" style={{ marginTop: '16px' }}>
            Any missed deadlines are shown here for your records.
          </Text>
          <YearEndListWithStatusIcons
            yearEndModules={missedModules}
            {...rest}
          />
        </div>
      )}
    </>
  )
}
export default YearEndModuleProgressList
